import { DriverLicense, LicensePlate } from '~/models/profile';
import {
    LicensePlateDetail,
    LicensePlateDetailResp,
    LicensePlateDetailUpdate,
    LicensePlateRenewal,
} from '~/store/types';

const pageUrls = {
    information: '/licence-plate-renewal/information',
    details: '/licence-plate-renewal/details',
    term: '/licence-plate-renewal/term',
    insurance: '/licence-plate-renewal/insurance',
    odometer: '/licence-plate-renewal/odometer',
    contact: '/licence-plate-renewal/contact',
    review: '/licence-plate-renewal/review',
    orderSummary: '/licence-plate-renewal/order-summary',
    makePayment: '/licence-plate-renewal/make-payment',
    dashBoard: '/dashboard',
    errorPage: '/licence-plate-renewal/avpv-error',
};

const {
    information,
    details,
    term,
    insurance,
    odometer,
    contact,
    review,
    orderSummary,
    makePayment,
    dashBoard,
    errorPage,
} = pageUrls;

export const informationUrl = information;
export const detailsUrl = details;
export const termUrl = term;
export const insuranceUrl = insurance;
export const odometerUrl = odometer;
export const contactUrl = contact;
export const reviewUrl = review;
export const orderSummaryUrl = orderSummary;
export const makePaymentUrl = makePayment;
export const dashBoardUrl = dashBoard;
export const errorPageUrl = errorPage;

export const OneTimeRunMillisecond = 1000;

export interface PlateNumbersMap {
    licensePlates: string[];
    permitNumbers: string[];
}

export const deepMerge = (target: any, source: any): any => {
    for (const key in source) {
        if (source.hasOwnProperty(key)) {
            if (typeof source[key] === 'object' && source[key] !== null) {
                if (!target[key]) {
                    target[key] = {};
                }
                deepMerge(target[key], source[key]);
            } else {
                target[key] = source[key];
            }
        }
    }
    return target;
};

export const oneTimeRun = <T extends (...args: any[]) => void>(
    func: T,
    wait: number
): ((...args: Parameters<T>) => void) => {
    let isThrottled = false;

    return function (this: any, ...args: Parameters<T>) {
        if (!isThrottled) {
            func.apply(this, args);
            isThrottled = true;

            setTimeout(() => {
                isThrottled = false;
            }, wait);
        }
    };
};

export const mapPlateNumbers = (data: LicensePlateDetail[], profileData: LicensePlate[]) => {
    const ret: PlateNumbersMap = { licensePlates: [], permitNumbers: [] };

    if (!data || !Array.isArray(data) || !profileData || !Array.isArray(profileData)) {
        return ret;
    }
    data.forEach(d => {
        if (d?.licensePlateNumber && d?.permitNumber) {
            if (!ret.licensePlates.includes(d?.licensePlateNumber)) {
                ret.licensePlates.push(d?.licensePlateNumber);
                ret.permitNumbers.push(d?.permitNumber);
            }
        }
    });

    if (profileData && profileData.length > 0) {
        profileData.forEach(p => {
            if (p?.number && p?.permitNumber && !ret.licensePlates.includes(p?.number)) {
                ret.licensePlates.push(p?.number);
                ret.permitNumbers.push(p?.permitNumber);
            }
        });
    }

    return ret;
};

export const mapPlateNumbersList = (data: LicensePlateDetail[]) => {
    const ret: PlateNumbersMap = { licensePlates: [], permitNumbers: [] };

    if (!data || !Array.isArray(data)) {
        return ret;
    }
    data.forEach(d => {
        if (d?.licensePlateNumber && d?.permitNumber) {
            if (!ret.licensePlates.includes(d?.licensePlateNumber)) {
                ret.licensePlates.push(d?.licensePlateNumber);
                ret.permitNumbers.push(d?.permitNumber);
            }
        }
    });

    return ret;
};

export const removeSpaces = (data: string, type = 'LicensePlate') => {
    switch (type) {
        case 'LicensePlate':
            return data.replace(/\s+/g, '');

        default:
            break;
    }

    return data.replace(/\s+/g, '');
};

export const removeAllDetailsSpaces = (details: LicensePlateDetail[]) => {
    return details.map(detail => {
        return { ...detail, licensePlateNumber: removeSpaces(detail.licensePlateNumber || '') };
    });
};

export const parseInquireResponse = (data: any) => {
    const item = data?.items?.item;

    const ret = {
        requireInsurance: item?.plateRenewal?.insuranceInfoRequired || false,
        requireOdometer: item?.plateRenewal?.odometerReadingRequired || false,
        requireTerms:
            (item?.plateRenewal?.termInfo && item?.plateRenewal?.termInfo.length > 1) || false,
        item: { transactionId: item?.transactionId, ...item?.plateRenewal },
    };

    return ret;
};

export const AvpvHardStopErrors = [
    'CXPLPR401',
    'CXPLPR402',
    'CXPLPR201',
    'CXPLPR204',
    'CXPLPR205',
    'CXPLPR207',
    'CXPLPR110',
    'CXPLPR015',
];

export const needShowErrorPage = (errorCode: string) => {
    return AvpvHardStopErrors.includes(errorCode);
};

export enum Term {
    ONE_YEAR = 1,
    TWO_YEARS = 2,
}

export const MaxCountOfPlates = 6;

export interface TTermsOption {
    licensePlateNumber: string;
    permitNumber: string;
    renewalTerm: number;
    productFee: {
        unitCost?: number;
        taxCode?: number;
        taxAmount?: number;
        totalCost: string;
    };
    expiryDate: string;
}

export interface TTermsList {
    licensePlateNumber: string;
    permitNumber: string;
    optionsTerm: TTermsOption[];
}

type CurrentPageType =
    | 'information'
    | 'details'
    | 'term'
    | 'insurance'
    | 'odometer'
    | 'contact'
    | 'review';

export const mapTermOptionsFromDetails = (data: Array<any>): Array<any> => {
    const ret: any[] = [];

    data.forEach((d: any) => {
        const terms = d?.inquireResponse?.termInfo;
        const licensePlateNumber = d.licensePlateNumber;
        const permitNumber = d.permitNumber;
        const optionsTerm: TTermsOption[] = [];
        const temp: TTermsList = { licensePlateNumber, permitNumber, optionsTerm };

        if (terms && terms.length > 0) {
            terms.forEach((t: any) => {
                temp.optionsTerm.push({
                    licensePlateNumber,
                    permitNumber,
                    renewalTerm: t?.renewalTerm || 1,
                    productFee: t?.itemCost?.productFee
                        ? {
                              ...t?.itemCost?.productFee,
                              totalCost: parseFloat(
                                  t?.itemCost?.productFee?.totalCost || '0'
                              ).toFixed(2),
                          }
                        : { ...t?.itemCost?.productFee, totalCost: 0 },
                    expiryDate: t?.nextValtagExpiryDate || '',
                });
            });
            ret.push(temp);
        }
    });

    return ret;
};

export const mapTermsFormData = (data: any) => {
    const preName = 'avpvGroup-insuranceTerm-';
    const ret: Array<LicensePlateDetail> = [];
    Object.keys(data).map((key: string) => {
        const plateNumber = (key || '').replace(preName, '');
        ret.push({ licensePlateNumber: plateNumber, renewalTerm: data[key] });
    });

    return ret;
};

export type InsuranceData = Record<string, any>;

interface MappedInsuranceData {
    licensePlateNumber: string;
    insuranceCompany: string;
    policyNumber: string;
}

export const mapInsuranceFormData = (data: InsuranceData): MappedInsuranceData[] => {
    const preName = 'insuranceCompany-';
    const preNamePolicyNumber = 'policyNumber-';
    const ret: MappedInsuranceData[] = [];
    const insuranceInfo: Record<string, { insuranceCompany?: string; policyNumber?: string }> = {};

    Object.keys(data).forEach(key => {
        if (key.includes(preName)) {
            const plateNumber = key.replace(preName, '');
            insuranceInfo[plateNumber] = {
                ...insuranceInfo[plateNumber],
                insuranceCompany: data[key],
            };
        }
        if (key.includes(preNamePolicyNumber)) {
            const plateNumber = key.replace(preNamePolicyNumber, '');
            insuranceInfo[plateNumber] = {
                ...insuranceInfo[plateNumber],
                policyNumber: data[key],
            };
        }
    });

    Object.keys(insuranceInfo).forEach(licensePlateNumber => {
        const { insuranceCompany, policyNumber } = insuranceInfo[licensePlateNumber];
        if (insuranceCompany && policyNumber) {
            ret.push({ licensePlateNumber, insuranceCompany, policyNumber });
        }
    });

    return ret;
};

export const getAVPVPageStatus = (data: Array<any>): LicensePlateDetailResp => {
    const requireTerms =
        data.filter((d: LicensePlateDetailResp) => d.requireTerms === true).length > 0;
    const requireInsurance =
        data.filter((d: LicensePlateDetailResp) => d.requireInsurance === true).length > 0;
    const requireOdometer =
        data.filter((d: LicensePlateDetailResp) => d.requireOdometer === true).length > 0;

    return { requireTerms, requireInsurance, requireOdometer };
};

export const calculateTotalSteps = (data: Array<any>): number => {
    let total = 5;

    const { requireTerms, requireInsurance, requireOdometer } = getAVPVPageStatus(data);

    if (requireTerms) {
        total++;
    }
    if (requireInsurance) {
        total++;
    }
    if (requireOdometer) {
        total++;
    }

    return total;
};

export const calculateCurrentStep = (currentPage: CurrentPageType, data: Array<any>): number => {
    let currentStep = 0;

    switch (currentPage) {
        case 'information':
            currentStep = 1;
            break;
        case 'details':
            currentStep = 2;
            break;
        case 'term':
        case 'insurance':
        case 'odometer':
        case 'contact':
        case 'review':
            {
                let step = calculateCurrentStep(
                    getAVPVPrevUrl(currentPage, data).replace(
                        '/licence-plate-renewal/',
                        ''
                    ) as CurrentPageType,
                    data
                );

                currentStep = step + 1;
            }
            break;

        default:
            break;
    }

    return currentStep;
};

export const getAVPVNextUrl = (currentPage: CurrentPageType, data: Array<any>): string => {
    let nextUrl = '';

    const { requireTerms, requireInsurance, requireOdometer } = getAVPVPageStatus(data);

    switch (currentPage) {
        case 'information':
            nextUrl = detailsUrl;
            break;
        case 'details':
            nextUrl = requireTerms
                ? termUrl
                : requireInsurance
                  ? insuranceUrl
                  : requireOdometer
                    ? odometerUrl
                    : contactUrl;
            break;
        case 'term':
            nextUrl = requireInsurance ? insuranceUrl : requireOdometer ? odometerUrl : contactUrl;
            break;
        case 'insurance':
            nextUrl = requireOdometer ? odometerUrl : contactUrl;
            break;
        case 'odometer':
            nextUrl = contactUrl;
            break;
        case 'contact':
            nextUrl = reviewUrl;
            break;

        default:
            break;
    }

    return nextUrl;
};

export const getAVPVPrevUrl = (currentPage: CurrentPageType, data: Array<any>): string => {
    let prevUrl = '';

    const { requireTerms, requireInsurance, requireOdometer } = getAVPVPageStatus(data);

    switch (currentPage) {
        case 'information':
        case 'details':
            prevUrl = informationUrl;
            break;
        case 'term':
            prevUrl = detailsUrl;
            break;
        case 'insurance':
            prevUrl = requireTerms ? termUrl : detailsUrl;
            break;
        case 'odometer':
            prevUrl = requireInsurance ? insuranceUrl : requireTerms ? termUrl : detailsUrl;
            break;
        case 'contact':
            prevUrl = requireOdometer
                ? odometerUrl
                : requireInsurance
                  ? insuranceUrl
                  : requireTerms
                    ? termUrl
                    : detailsUrl;
            break;

        case 'review':
            prevUrl = contactUrl;
            break;

        default:
            break;
    }

    return prevUrl;
};

export const formatCurrency = (number: number, lang: string, currency = 'CAD'): string => {
    return new Intl.NumberFormat(lang === 'FR' ? 'fr-FR' : 'en-US', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
    }).format(number);
};

export type OdometerFormData = {
    [key: string]: string | undefined;
};

export const mapOdometerFormData = (data: any) => {
    const preName = 'odometer-';
    const ret: Array<LicensePlateDetail> = [];
    Object.keys(data).map((key: string) => {
        const plateNumber = (key || '').replace(preName, '');
        ret.push({ licensePlateNumber: plateNumber, odometerReading: data[key] });
    });

    return ret;
};

export const mapOdometerDefaultDataFromContextDetails = (details: Array<LicensePlateDetail>) => {
    let ret: OdometerFormData = {};
    const preStr = 'odometer-';

    details.forEach((d: LicensePlateDetail) => {
        ret[`${preStr}${d.licensePlateNumber || ''}`] = d.odometerReading || '';
    });

    return ret;
};

export const mapInsuranceDefaultDataFromContextDetails = (details: Array<LicensePlateDetail>) => {
    let ret: any = {};
    const preStrCompany = 'insuranceCompany-';
    const preStrPolicy = 'policyNumber-';

    details.forEach((d: LicensePlateDetail) => {
        ret[`${preStrCompany}${d.licensePlateNumber || ''}`] = d.insuranceCompany || '';
        ret[`${preStrPolicy}${d.licensePlateNumber || ''}`] = d.policyNumber || '';
    });

    return ret;
};

export const mapDefaultFirstDataFromContextDetails = (details: Array<LicensePlateDetail>) => {
    return {
        licensePlateNumber: details[0].licensePlateNumber || '',
        permitNumber: details[0].permitNumber || '',
    };
};

export const mapTermDefaultDataFromContextDetails = (details: Array<LicensePlateDetail>) => {
    let ret: any = {};
    const preStr = 'avpvGroup-insuranceTerm-';

    details.forEach((d: LicensePlateDetail) => {
        ret[`${preStr}${d.licensePlateNumber || ''}`] = d.renewalTerm + '' || '';
    });

    return ret;
};

export const getSuccessfulLicensePlates = (items: [any]) => {
    if (!items) {
        return [];
    }

    return items.filter(item => item.success).map(item => item.licensePlateNumber);
};

export const mapDetailsToUpdateProfiles = async (
    details: Array<LicensePlateDetail>,
    items: [any],
    productLicensePlates: [any],
    driversInfo: DriverLicense
) => {
    const successfulLicensePlates = getSuccessfulLicensePlates(items);

    return details
        .filter(
            (f: any) =>
                successfulLicensePlates.includes(f?.licensePlateNumber) &&
                !productLicensePlates.some((item: any) => item.number === f?.licensePlateNumber)
        )
        .map((d: any) => {
            return {
                number: removeSpaces(d?.licensePlateNumber),
                permitNumber: d?.permitNumber,
                driverLicenseNumber: driversInfo?.number,
                driverLicenseProductId: driversInfo?.productId,
                productId: 0,
                subscribeRR: true,
            };
        });
};

export const removeDetail = (prevDetails: Array<LicensePlateDetail>, removed: LicensePlateDetail) =>
    prevDetails.reduce((acc: LicensePlateDetail[], curr) => {
        if (
            curr.licensePlateNumber !== removed.licensePlateNumber &&
            curr.permitNumber !== removed.permitNumber
        ) {
            acc.push(curr);
        }

        return acc;
    }, []);

export const updateDetail = (
    prevDetails: Array<LicensePlateDetail>,
    newDetailResp: LicensePlateDetailUpdate
) =>
    prevDetails.reduce((acc, detail: LicensePlateDetail) => {
        if (
            detail.licensePlateNumber === newDetailResp.licensePlateNumber &&
            detail.permitNumber === newDetailResp.permitNumber
        ) {
            acc.push(deepMerge(detail, { ...newDetailResp?.newDetailResp }));
        } else {
            acc.push(detail);
        }

        return acc;
    }, [] as LicensePlateDetail[]);

export const isDuplicatedDetail = (licensePlate: string, details: Array<LicensePlateDetail>) => {
    return !!details.find(d => d.licensePlateNumber === licensePlate);
};

export const handleEmailFormData = (email: string, data: LicensePlateRenewal) => {
    return {
        emailAddress: email,
        cxpTransactionId: data?.orderStatus?.cxpTransactionId,
        paymentTransactionId: data?.orderStatus?.payment?.transactionId,
    };
};

export const formatTorontoDate = (isoDate: string): string => {
    const date = new Date(isoDate);

    const options = {
        timeZone: 'America/New_York',
        year: 'numeric' as const,
        month: 'long' as const,
        day: 'numeric' as const,
    };
    try {
        const formatter = new Intl.DateTimeFormat('en-US', options);

        return formatter.format(date);
    } catch (err) {
        return isoDate;
    }
};

export const notAllowToAddMorePlates = (details: Array<LicensePlateDetail>): boolean => {
    return details && details.length >= MaxCountOfPlates;
};

export const getMTOErrorCodesList = (): string[] => {
    return [
        'CXPLPR101',
        'CXPLPR102',
        'CXPLPR103',
        'CXPLPR104',
        'CXPLPR105',
        'CXPLPR106',
        'CXPLPR107',
        'CXPLPR108',
    ];
};
export const errorCodesFromMTO = (errorCode: string): boolean => {
    return getMTOErrorCodesList().includes(errorCode);
};

export const handlePrintFormData = (data: LicensePlateRenewal) => {
    return {
        cxpTransactionId: data?.orderStatus?.cxpTransactionId,
        paymentTransactionId: data?.orderStatus?.payment?.transactionId,
    };
};

export interface LicensePlateError {
    errorCode: string;
    licensePlate: string;
}

export interface GroupedLicensePlateError {
    errorCode: string;
    licensePlate: string;
}

export const groupLicensePlatesByErrorCode = (
    data: LicensePlateError[]
): GroupedLicensePlateError[] => {
    const grouped = data.reduce(
        (acc, current) => {
            if (!acc[current.errorCode]) {
                acc[current.errorCode] = [];
            }
            acc[current.errorCode].push(current.licensePlate);
            return acc;
        },
        {} as Record<string, string[]>
    );

    return Object.entries(grouped).map(([errorCode, licensePlates]) => ({
        errorCode,
        licensePlate: licensePlates.join(', '),
    }));
};

export const mapErrorCodesFromUpdateProfiles = (data: [any]) => {
    console.log(data);
    const errors = data.map((d: any) => {
        return {
            errorCode: d?.error?.errorCodes[0],
            licensePlate: d?.item?.number,
        };
    });

    return groupLicensePlatesByErrorCode(errors);
};
